import { useAlerts } from "components/Alerts/AlertProvider";
import {
  buildOrderedConfig,
  toReviewerInputs,
} from "components/Submission/helpers/reviewerConfiguration";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { ReviewerStatus, ReviewMode, SignatureType } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useUpdateSubmission } from "hooks/useSubmissionMutations";
import { useCallback } from "react";

/** Staff mark-complete/reset override: persists a reviewer status change immediately, outside the normal save flow. */
export function useReviewerStatusOverrideHandlers({
  updateSubmission,
  submissionId,
  signatureType,
  reviewRequired,
  peerReviewRequired,
  reviewMode,
  signer,
  alerts,
  reviewers,
  setReviewers,
  finalApprovalReviewers,
  setFinalApprovalReviewers,
}: {
  updateSubmission: ReturnType<typeof useUpdateSubmission>[0];
  submissionId: string;
  signatureType: SignatureType;
  reviewRequired: boolean;
  peerReviewRequired: boolean;
  reviewMode: ReviewMode;
  signer: SubmissionReviewerSigner | null;
  alerts: ReturnType<typeof useAlerts>;
  reviewers: Reviewer[];
  setReviewers: (next: Reviewer[]) => void;
  finalApprovalReviewers: Reviewer[] | null;
  setFinalApprovalReviewers: (next: Reviewer[] | null) => void;
}) {
  const persistReviewerOverride = useCallback(
    async (next: Reviewer[]) => {
      setReviewers(next);
      try {
        await updateSubmission({
          variables: {
            input: {
              id: submissionId,
              signatureType,
              reviewRequired,
              peerReviewRequired,
              reviewMode,
              reviewers: toReviewerInputs(buildOrderedConfig(next, signer)),
            },
          },
        });
        alerts.success("Reviewer status saved.");
      } catch (error) {
        alerts.error("Failed to save the reviewer status.", error);
      }
    },
    [
      updateSubmission,
      submissionId,
      signatureType,
      reviewRequired,
      peerReviewRequired,
      reviewMode,
      signer,
      alerts,
      setReviewers,
    ]
  );

  // Adds/reorders/deletes stay local until save/send; a reviewerStatus change is
  // the staff override and persists immediately.
  const handleReviewersDatagridChange = useCallback(
    (next: Reviewer[]) => {
      const statusChanged = next.some((r) => {
        const prev = reviewers.find((p) => p.id === r.id);
        return prev != null && prev.reviewerStatus !== r.reviewerStatus;
      });
      if (statusChanged) {
        const orderedReviewers = buildOrderedConfig(next, signer);
        const completesAllApprovals =
          orderedReviewers.length > 0 &&
          orderedReviewers.every((reviewer) => reviewer.reviewerStatus === ReviewerStatus.Approved);
        if (completesAllApprovals) {
          setFinalApprovalReviewers(next);
          return;
        }
        void persistReviewerOverride(next);
        return;
      }
      setReviewers(next);
    },
    [reviewers, signer, persistReviewerOverride, setFinalApprovalReviewers, setReviewers]
  );

  const handleConfirmFinalApproval = async () => {
    if (!finalApprovalReviewers) return;
    await persistReviewerOverride(finalApprovalReviewers);
    setFinalApprovalReviewers(null);
  };

  return { handleReviewersDatagridChange, handleConfirmFinalApproval };
}
